/**
 * Parcours d'une délibération, pas à pas — refonte UX, chantier 5 (maquette 9).
 *
 * Données RÉELLES : le calendrier du parcours (dates attendues par étape,
 * dépassement — calculés par le serveur, mig 273) et les pièces (exigées,
 * déposées — D2). En plus, la date limite de CONVOCATION du conseil, calculée à
 * partir de la date de séance : 5 jours francs dans les communes de 3 500
 * habitants et plus (CGCT L2121-12), 3 jours francs en dessous (L2121-11).
 * Jours francs : ni le jour d'envoi ni celui de la séance ne comptent — séance
 * le jeudi 8, convocation au plus tard le vendredi 2 (5 jours francs).
 * La branche « Rejetée » n'apparaît que si le parcours l'a prise.
 */
const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const fr = (t: Date) => `${JOURS[t.getUTCDay()]} ${t.getUTCDate()} ${MOIS[t.getUTCMonth()]}`;
const date = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`);

interface Etape { code: string; libelle: string; ordre: number; dateAttendue: string | null; depassee: boolean }
interface Piece { libelle: string; exigee: boolean; deposees: unknown[] }

export function ParcoursDeliberation({ etatCode, calendrier, pieces, dateSeance }: {
  etatCode: string; calendrier: Etape[]; pieces: Piece[]; dateSeance: string | null;
}) {
  const etapes = [...calendrier].sort((a, b) => a.ordre - b.ordre)
    .filter((e) => !e.code.startsWith('rejet') || e.code === etatCode);
  const courante = etapes.find((e) => e.code === etatCode);
  const exigees = pieces.filter((p) => p.exigee);
  const pretes = exigees.filter((p) => p.deposees.length > 0).length;
  const seance = dateSeance ? date(dateSeance) : null;
  const convoc = (joursFrancs: number) => seance ? new Date(seance.getTime() - (joursFrancs + 1) * 86_400_000) : null;

  return (
    <div className="mb-3 space-y-3">
      <ol aria-label="Étapes de la délibération" className="flex flex-wrap items-stretch gap-1.5">
        {etapes.map((e, i) => {
          const faite = courante ? e.ordre < courante.ordre : false;
          const enCours = e.code === etatCode;
          return (
            <li key={e.code} className="flex items-stretch gap-1.5" aria-current={enCours ? 'step' : undefined}>
              {i > 0 && <span aria-hidden="true" className="self-center text-ink-subtle">→</span>}
              <div className={`w-40 rounded-r-lg border px-2.5 py-2 ${enCours ? 'border-grc bg-grc/10' : faite ? 'border-success/40 bg-success/5' : 'border-border'}`}>
                <p className={`text-2xs ${enCours ? 'text-grc-fg' : faite ? 'text-success' : 'text-ink-subtle'}`}>
                  {faite ? '✓ Fait' : enCours ? 'En cours' : 'À venir'}
                </p>
                <p className="text-xs font-medium leading-snug text-ink">{e.libelle}</p>
                {e.dateAttendue && (
                  <p className={`text-2xs ${e.depassee && !faite ? 'font-medium text-urgent' : 'text-ink-muted'}`}>
                    {e.depassee && !faite ? 'Dépassé : ' : ''}{fr(date(e.dateAttendue))}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      <div className="flex flex-wrap gap-3 text-xs">
        {exigees.length > 0 && (
          <span className={`rounded-r-pill px-2.5 py-1 font-medium ${pretes === exigees.length ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning'}`}>
            Pièces : {pretes} sur {exigees.length} prête{exigees.length > 1 ? 's' : ''}
          </span>
        )}
        {seance && (
          <span className="rounded-r-pill bg-tech/10 px-2.5 py-1 text-ink">
            Convocation au plus tard le <b>{fr(convoc(5)!)}</b> (5 jours francs, communes de 3 500 hab. et plus) · <b>{fr(convoc(3)!)}</b> en dessous (3 jours francs)
          </span>
        )}
      </div>
    </div>
  );
}
